import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, User, ShoppingBag } from "lucide-react";
import { Container } from "@/components/ui/layout";
import { Button } from "@/components/ui/button";
import { BrandLogo } from "@/components/brand-logo";
import { useAuth } from "@/hooks/use-auth";
import { useCart } from "@/hooks/use-cart";
import { cn } from "@/lib/utils";

const links = [
  { to: "/shop", label: "Shop" },
  { to: "/shop?category=chutney-house", label: "Chutney House" },
  { to: "/shop?category=aachar-house", label: "Aachar House" },
  { to: "/#story", label: "Our Story" },
];

export function SiteHeader() {
  const { user, logout } = useAuth();
  const { cart, setOpen } = useCart();
  const [open, setMenuOpen] = useState(false);
  const count = cart?.itemCount ?? 0;

  const CartButton = (
    <button
      type="button"
      className="relative cursor-pointer p-2 text-foreground transition-colors hover:text-primary"
      aria-label={`Open cart${count ? `, ${count} items` : ""}`}
      onClick={() => setOpen(true)}
    >
      <ShoppingBag className="h-5 w-5" />
      {count > 0 ? (
        <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent px-1 text-[10px] font-semibold text-foreground">
          {count > 99 ? "99+" : count}
        </span>
      ) : null}
    </button>
  );

  return (
    <header className="sticky top-0 z-40 border-b border-border/70 bg-background/90 backdrop-blur-md">
      <Container className="flex h-20 items-center justify-between md:h-24">
        <BrandLogo size="sm" showHouse houseLabel="" />

        <nav className="hidden items-center gap-8 md:flex" aria-label="Primary">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              className={({ isActive }) =>
                cn(
                  "text-sm text-muted transition-colors duration-200 hover:text-foreground cursor-pointer",
                  isActive && "text-foreground",
                )
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-3 md:flex">
          {CartButton}
          {user ? (
            <>
              {user.role === "admin" ? (
                <Link
                  to="/admin"
                  className="text-sm text-muted hover:text-foreground cursor-pointer"
                >
                  Admin
                </Link>
              ) : null}
              <Link
                to="/account"
                className="inline-flex items-center gap-2 text-sm text-muted hover:text-foreground cursor-pointer"
              >
                <User className="h-4 w-4" aria-hidden />
                {user.name.split(" ")[0]}
              </Link>
              <Button variant="ghost" size="sm" onClick={() => void logout()}>
                Logout
              </Button>
            </>
          ) : (
            <>
              <Link
                to="/login"
                className="text-sm text-muted transition-colors hover:text-foreground cursor-pointer"
              >
                Sign in
              </Link>
              <Link to="/shop">
                <Button size="sm">Shop Now</Button>
              </Link>
            </>
          )}
        </div>

        <div className="flex items-center gap-1 md:hidden">
          {CartButton}
          <button
            type="button"
            className="cursor-pointer p-2 text-foreground"
            aria-label={open ? "Close menu" : "Open menu"}
            onClick={() => setMenuOpen((v) => !v)}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </Container>

      {open ? (
        <div className="border-t border-border bg-background md:hidden">
          <Container className="flex flex-col gap-4 py-5">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="text-base text-foreground cursor-pointer"
                onClick={() => setMenuOpen(false)}
              >
                {l.label}
              </Link>
            ))}
            <button
              type="button"
              className="text-left text-base text-foreground cursor-pointer"
              onClick={() => {
                setMenuOpen(false);
                setOpen(true);
              }}
            >
              Cart{count ? ` (${count})` : ""}
            </button>
            <Link to="/shop" onClick={() => setMenuOpen(false)}>
              <Button className="w-full">Shop Now</Button>
            </Link>
            {user ? (
              <>
                {user.role === "admin" ? (
                  <Link to="/admin" onClick={() => setMenuOpen(false)}>
                    Admin
                  </Link>
                ) : null}
                <Link to="/account" onClick={() => setMenuOpen(false)}>
                  Account
                </Link>
                <Button variant="outline" onClick={() => void logout()}>
                  Logout
                </Button>
              </>
            ) : (
              <Link to="/login" onClick={() => setMenuOpen(false)}>
                <Button variant="outline" className="w-full">
                  Sign in
                </Button>
              </Link>
            )}
          </Container>
        </div>
      ) : null}
    </header>
  );
}
